import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Book.css";

const ChevronLeft = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15,18 9,12 15,6"></polyline>
  </svg>
);

const ChevronRight = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9,18 15,12 9,6"></polyline>
  </svg>
);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
};

const useBookPages = () => {
  const [bookPages, setBookPages] = useState([]);

  useEffect(() => {
    fetch("https://port-0-working-task-madmcado69392982.sel4.cloudtype.app/curations/all")
      .then((res) => res.json())
      .then((data) => {
        const sorted = [...data].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        const pages = sorted.map((item) => ({
          title: item.title,
          chapter: formatDate(item.created_at),
          content: item.content.join("\n"),
          link: item.link,
        }));

        setBookPages(pages);
      })
      .catch((err) => console.error("책 데이터를 불러오는 중 오류:", err));
  }, []);

  return bookPages;
};

const Page = ({ page, pageNumber }) => {
  return (
    <div className="page">
      <div className="page-content">
        <div className="page-chapter">{page.chapter}</div>
        <h1 className="page-title">{page.title}</h1>
        <div className="page-text">
          {page.content.split("\n").map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
        {page.link && (
          <a
            href={page.link}
            target="_blank"
            rel="noopener noreferrer"
            className="page-link"
          >
            인스타그램 바로가기
          </a>
        )}
        <div className="page-number">{pageNumber}</div>
      </div>
    </div>
  );
};

const BookNavigation = ({ currentPage, totalPages, onPageChange }) => {
  return (
    <div className="page-indicator">
      {Array.from({ length: totalPages }, (_, index) => (
        <button
          key={index}
          onClick={() => onPageChange(index)}
          className={`page-dot ${index === currentPage ? "active" : ""}`}
          aria-label={`페이지 ${index + 1}로 이동`}
          title={`페이지 ${index + 1}`}
        />
      ))}
    </div>
  );
};

export const Book = () => {
  const bookPages = useBookPages();
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();

  const nextPage = useCallback(() => {
    setCurrentPage((prev) => {
      if (prev < bookPages.length - 1) return prev + 1;
      return prev;
    });
  }, [bookPages.length]);

  const prevPage = useCallback(() => {
    setCurrentPage((prev) => {
      if (prev > 0) return prev - 1;
      return prev;
    });
  }, []);

  const goToPage = (pageIndex) => {
    if (pageIndex >= 0 && pageIndex < bookPages.length) {
      setCurrentPage(pageIndex);
    }
  };

  const handleClose = () => {
    navigate("/home");
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        nextPage();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevPage();
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [nextPage, prevPage]);

  if (bookPages.length === 0) {
    return (
      <div className="book-wrapper">
        <div className="loading-text">데이터를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="book-wrapper">
      <div className="book-container">
        <div className="book-header">
          <h1>큐레이션북</h1>
        </div>

        <button
          onClick={prevPage}
          disabled={currentPage === 0}
          className="navigation-button left"
          aria-label="이전 페이지"
        >
          <ChevronLeft />
        </button>

        <button
          onClick={nextPage}
          disabled={currentPage === bookPages.length - 1}
          className="navigation-button right"
          aria-label="다음 페이지"
        >
          <ChevronRight />
        </button>

        <div className="book">
          <button className="close-button" onClick={handleClose}>
            X
          </button>

          <div className="book-spine"></div>
          <div className="book-pages">
            <Page
              page={bookPages[currentPage]}
              pageNumber={currentPage + 1}
            />
          </div>
        </div>

        <BookNavigation
          currentPage={currentPage}
          totalPages={bookPages.length}
          onPageChange={goToPage}
        />

        <div className="book-footer">
          <p>화살표 버튼을 눌러 큐레이션 카드를 확인해보세요.</p>
        </div>
      </div>
    </div>
  );
};

export default Book;
